// @ts-check

/**
 * A combination of query blocks that define an event selector
 */
import {Component} from 'panel';
import pick from 'lodash/pick';

import {propagateEvent} from '../../util/dom';
import {ResourceType} from '../property-filter-menu/models/types';
import {registerMPElement} from 'common/util/register-element';
import {EVENT_PROPERTIES} from 'common/widgets/query-block/events-menu/types';
import {PropertyItem} from '../query-block/properties-menu/types';

import {QueryEntry, FilterOperator, SelectorTypes, SelectorTypeDefinitions, isPropertySelectorType} from './types';

import template from './index.jade';
import css from './index.styl?inline';

export default registerMPElement(
  `mp-query-event-selector`,
  class QueryEventSelector extends Component<object> {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'data-group-id': `string`,
        'data-groups-enabled': `boolean`,
        'disable-event-selection': `boolean`,
        'exclude-autotrack-events': {
          type: `boolean`,
          default: false,
          description: `whether to hide autotrack events`,
        },
        'enable-behavioral-properties': {type: `boolean`, default: false},
        'exclude-close-selectors': {type: `json`, default: []},
        'include-custom-properties': {
          type: `string`,
          enum: [`none`, `persisted-only`, `all`],
          default: `none`,
          description: `whether to hide custom properties from properties menus`,
        },
        'is-blocked-people': `boolean`,
        open: `boolean`,
        'project-has-people': `boolean`,
        'project-has-events': `boolean`,
        'project-id': {
          type: `number`,
          default: null,
        },
        'selected-event': {
          type: `json`,
          description: `interface: EventItem`,
        },
        'selected-filters': {
          type: `json`,
          default: [],
          description: `interface: EventFilters`,
        },
        'selected-filters-operator': {
          type: `string`,
          enum: Object.values(FilterOperator),
          default: FilterOperator.And,
        },
        'show-any-event': `boolean`,
        'hide-edit-controls': {
          type: `boolean`,
          description: `show controls that popup modals to edit entities like custom events and properties`,
        },
        'show-session-event': `boolean`,
        'show-all-events': `boolean`,
        'show-special-date-property': {
          type: `boolean`,
          description: `enables special date property and breakdown type for datetime`,
          default: false,
        },
        'show-special-date-property-unit': {
          type: `boolean`,
          description: `enables the unit selection for special date property break-down`,
          default: true,
        },
        'show-top-events': `boolean`,
        'show-type-icons': `boolean`,
        'show-placeholder': `boolean`,
        'support-b2b-cohorts': `boolean`,
        'support-inline-cohorts': `boolean`,
        'user-id': `number`,
        type: {
          type: `string`,
          enum: SelectorTypes as string[],
          default: ResourceType.Event,
          description: `type of selection`,
        },
        'workspace-id': `number`,
      };
    }

    get config() {
      return {
        css,
        template,
        useShadowDom: true,

        helpers: {
          BOOLEAN_OPERATORS: Object.values(FilterOperator),
          ResourceType,

          getQueryBlockConfig: () => this.getQueryBlockConfig(),
          hasSelectedEvent: () => this.selectionObject.event && this.selectionObject.event.value,
          onFilterSelected: (ev) => this.emitSelectionEvent({filters: ev.detail.items}),
          onFiltersOperatorSelected: (ev) => this.emitSelectionEvent({filtersOperator: ev.detail.value}),
          propagateEvent: (ev) => propagateEvent(this.app, ev),

          /** Updates event and resets any selected filters */
          onEventSelected: (ev) => {
            const type = this.attr(`type`);
            const {value} = ev.detail.item;

            this.emitSelectionEvent({
              ...(this.selectionObject.event.value === value ? {} : {filters: []}),
              ...(isPropertySelectorType(type) ? {property: ev.detail.item as PropertyItem} : null),
              event: pick(ev.detail.item, EVENT_PROPERTIES),
            });
          },
        },
      };
    }

    get selectionObject(): QueryEntry {
      return {
        event: this.attr(`selected-event`),
        filters: this.attr(`selected-filters`),
        filtersOperator: this.attr(`selected-filters-operator`),
        type: this.attr(`type`),
      };
    }

    private getQueryBlockConfig(): object {
      const type = this.attr(`type`);

      switch (type) {
        case ResourceType.Event:
          return {
            type: `events`,
            'exclude-autotrack-events': this.attr(`exclude-autotrack-events`),
            'include-custom-properties': this.attr(`include-custom-properties`),
            'is-blocked-people': this.attr(`is-blocked-people`),
            'project-has-people': this.attr(`project-has-people`),
            'show-all-events': this.attr(`show-all-events`),
            'show-any-event': this.attr(`show-any-event`),
            'show-session-event': this.attr(`show-session-event`),
            'show-top-events': this.attr(`show-top-events`),
          };
        case ResourceType.Cohort:
          return {
            type: `properties`,
            'include-custom-properties': this.attr(`include-custom-properties`),
            'project-has-cohorts': true,
            'project-has-events': false,
            'project-has-people': false,
            'support-b2b-cohorts': this.attr(`support-b2b-cohorts`),
            'support-inline-cohorts': this.attr(`support-inline-cohorts`),
          };
        case ResourceType.User:
          return {
            type: `properties`,
            'include-custom-properties': this.attr(`include-custom-properties`),
            'project-has-cohorts': false,
            'project-has-events': false,
            'project-has-people': true,
          };
        case SelectorTypeDefinitions.EventOrUserProperty:
          return {
            'allow-typecast': true,
            type: `properties`,
            'include-custom-properties': this.attr(`include-custom-properties`),
            'project-has-cohorts': false,
            'project-has-events': true,
            'project-has-people': true,
            'show-all-tab': true,
          };
        case SelectorTypeDefinitions.UserProperty:
          return {
            'allow-typecast': true,
            type: `properties`,
            'include-custom-properties': this.attr(`include-custom-properties`),
            'project-has-cohorts': false,
            'project-has-events': false,
            'project-has-people': true,
          };
        case SelectorTypeDefinitions.Behavior:
          return {
            type: `events`,
            'include-custom-properties': this.attr(`include-custom-properties`),
            'project-has-events': true,
            'project-has-people': true,
            'show-all-events': this.attr(`show-all-events`),
            'show-any-event': this.attr(`show-any-event`),
            'show-session-event': this.attr(`show-session-event`),
            'show-top-events': this.attr(`show-top-events`),
          };
        case SelectorTypeDefinitions.EventLaunch:
          return {
            type: `events`,
            'exclude-autotrack-events': this.attr(`exclude-autotrack-events`),
            'include-custom-properties': this.attr(`include-custom-properties`),
            'is-blocked-people': this.attr(`is-blocked-people`),
            'project-has-people': this.attr(`project-has-people`),
            'show-all-events': this.attr(`show-all-events`),
            'show-any-event': this.attr(`show-any-event`),
            'show-session-event': this.attr(`show-session-event`),
            'show-top-events': this.attr(`show-top-events`),
          };
      }

      return {};
    }

    /**
     * Consolidates updated event changes into a single object
     */
    private emitSelectionEvent(updates) {
      const detail = {...this.selectionObject, ...updates};
      this.dispatchEvent(new CustomEvent(`select`, {detail}));
    }
  },
);
